import { Palette, Type } from 'lucide-react';
import React, { useState } from 'react';
import { Customization } from '../../types';
import './StyleCustomization.css';

interface StyleCustomizationProps {
  customization: Customization;
  onCustomizationChange: (customization: Customization) => void;
}

const StyleCustomization: React.FC<StyleCustomizationProps> = ({
  customization,
  onCustomizationChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const colorPresets = [
    { name: 'Professional Blue', primary: '#4F46E5', accent: '#818CF8' },
    { name: 'Classic Black', primary: '#000000', accent: '#4B5563' },
    { name: 'Modern Teal', primary: '#14B8A6', accent: '#5EEAD4' },
    { name: 'Corporate Navy', primary: '#1E3A8A', accent: '#3B82F6' },
    { name: 'Elegant Purple', primary: '#7C3AED', accent: '#A78BFA' },
  ];

  const fontOptions = [
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Times New Roman, serif', label: 'Times New Roman' },
    { value: 'Calibri, sans-serif', label: 'Calibri' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  ];


  const updateColor = (type: 'primary' | 'accent', color: string) => {
    onCustomizationChange({
      ...customization,
      colors: {
        ...customization.colors,
        [type]: color,
      },
    });
  };

  const applyColorPreset = (preset: { primary: string; accent: string }) => {
    onCustomizationChange({
      ...customization,
      colors: {
        primary: preset.primary,
        accent: preset.accent,
      },
    });
  };

  const updateFont = (type: 'headerFont' | 'bodyFont', font: string) => {
    onCustomizationChange({
      ...customization,
      fonts: {
        ...customization.fonts,
        [type]: font,
      },
    });
  };

  const updateFontSize = (size: number) => {
    onCustomizationChange({
      ...customization,
      fonts: {
        ...customization.fonts,
        fontSize: size,
      },
    });
  };

  if (!isExpanded) {
    return (
      <div className="style-customization-section">
        <button onClick={() => setIsExpanded(true)} className="section-expand-btn">
          <Palette size={18} />
          <span>Colors & Fonts</span>
        </button>
      </div>
    );
  }

  return (
    <div className="style-customization-section expanded">
      <div className="section-header-bar">
        <div className="section-title-group">
          <Palette size={18} />
          <h3>Colors & Fonts</h3>
        </div>
        <button onClick={() => setIsExpanded(false)} className="section-collapse-btn">
          Collapse
        </button>
      </div>

      {/* Color Presets */}
      <div className="customization-group">
        <label className="customization-label">Color Presets</label>
        <div className="color-presets-grid">
          {colorPresets.map((preset, index) => (
            <button
              key={index}
              onClick={() => applyColorPreset(preset)}
              className="color-preset-btn"
              title={preset.name}
            >
              <div className="preset-colors">
                <div className="preset-color" style={{ backgroundColor: preset.primary }} />
                <div className="preset-color" style={{ backgroundColor: preset.accent }} />
              </div>
              <span className="preset-label">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="customization-group">
        <label className="customization-label">Custom Colors</label>
        
        <div className="color-pickers">
          <div className="color-picker-item">
            <label>Primary</label>
            <input
              type="color"
              value={customization.colors?.primary || '#4F46E5'}
              onChange={(e) => updateColor('primary', e.target.value)}
              className="color-input"
            />
            <span className="color-hex">{customization.colors?.primary || '#4F46E5'}</span>
          </div>

          <div className="color-picker-item">
            <label>Accent</label>
            <input
              type="color"
              value={customization.colors?.accent || '#818CF8'}
              onChange={(e) => updateColor('accent', e.target.value)}
              className="color-input"
            />
            <span className="color-hex">{customization.colors?.accent || '#818CF8'}</span>
          </div>
        </div>
      </div>

      {/* Fonts */}
      <div className="customization-group">
        <div className="customization-label-group">
          <Type size={16} />
          <label className="customization-label">Typography</label>
        </div>

        <div className="font-selectors">
          <div className="form-group">
            <label>Header Font</label>
            <select
              value={customization.fonts?.headerFont || 'Arial, sans-serif'}
              onChange={(e) => updateFont('headerFont', e.target.value)}
              className="form-select"
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Body Font</label>
            <select
              value={customization.fonts?.bodyFont || 'Arial, sans-serif'}
              onChange={(e) => updateFont('bodyFont', e.target.value)}
              className="form-select"
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Font Size (pt)</label>
            <input
              type="number"
              min={8}
              max={14}
              value={customization.fonts?.fontSize || 10}
              onChange={(e) => updateFontSize(parseInt(e.target.value) || 10)}
              className="form-input font-size-input"
            />
          </div>
        </div>
      </div>

      {/* Reset */}
      <button
        onClick={() => onCustomizationChange({
          ...customization,
          colors: { primary: '#4F46E5', accent: '#818CF8' },
          fonts: { headerFont: 'Arial, sans-serif', bodyFont: 'Arial, sans-serif', fontSize: 10 },
        })}
        className="btn-reset"
      >
        Reset to Default
      </button>
    </div>
  );
};

export default StyleCustomization;

